import { AfterViewChecked, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { COPILOT_DISCLAIMER } from '../../copilot-shared/copilot-disclaimer';
import { COPILOT_DISCLAIMER_ENABLED } from '../../copilot-shared/copilot-disclaimer-visibility';
import { compareLegStrikeDesc, Leg, legStrikeForSort, Solution, SolutionContent } from './copilot-domain';

// A card inside a package, plus its own open/closed state. Only the CARDS collapse — the package
// around them is always fully drawn (owner ruling, vrequest 2026-07-27).
interface PackageCardVm {
    title: string;
    legs: Leg[];
    expanded: boolean;
}
import { IHedgingGridCopilotFacade } from './copilot-facade';
import { CoPilotPositionDto } from './copilot-operations';
import { HgCopilotTransactionsPopupComponent } from './hg-copilot-transactions-popup.component';
import { LastQuoteCacheService } from '../../last-quote-cache.service';
import { MessageBusService } from '../../message-bus.service';
import { TradingInstrumentDisplayNameService } from '../../trading-instruments/trading-instrument-display-name.service';
import { QuoteDto } from '../../shell-communication/dtos/quote-dto.class';
import { ComboNet, computeComboNet, formatMoney as formatComboMoney, ledgerPrice, orderPrice } from './copilot-pricing';
import { DetectMethodChanges, isVoid } from '../../utils';
import { AdjustmentProposal } from './available-adjustment-model';

// One Solution card (FR-53/56/58..69). Renders the skill-authored content VERBATIM, read-only
// (host never edits/re-judges). Exactly THREE actions — Preview / Apply / Transactions — and NO
// broker path. Apply/Block target the host identity (solution.id), never the skill content.
//
// Default change detection (NOT OnPush): the parent mutates solution.state in place via the
// Apply/Block state machine, then calls detectChanges(); a default-CD card re-renders reliably,
// whereas an OnPush card would be skipped on in-place mutation.
@Component({
    selector: 'ets-hg-copilot-solution-card',
    templateUrl: 'hg-copilot-solution-card.component.html',
    styleUrls: ['./hg-copilot-solution-card.component.scss']
})
export class HgCopilotSolutionCardComponent implements OnInit, OnDestroy, AfterViewChecked {

    constructor(
        private readonly _changeDetector: ChangeDetectorRef,
        private readonly _lastQuoteCache: LastQuoteCacheService,
        private readonly _messageBus: MessageBusService,
        private readonly _displayNames: TradingInstrumentDisplayNameService
    ) {
    }

    // M8: the shared compliance disclaimer, rendered under the action row. A constant, never
    // reassigned — exposed as a field only because templates cannot import.
    readonly disclaimer = COPILOT_DISCLAIMER;

    // CR-20260723-003: global visibility switch for the disclaimer above (client-requested disable).
    // HIDDEN, not removed - flip COPILOT_DISCLAIMER_ENABLED to re-show the disclaimer at all five sites.
    readonly disclaimerEnabled = COPILOT_DISCLAIMER_ENABLED;

    private readonly _unsubscriber = new Subject<void>();

    @Input() solution: Solution;

    // The grid facade (same [ctx] the orchestrator holds) — supplies the net-price normalizers:
    // getNormFactor(asset) (per-leg ES->SPX) + readDefaultQty() (per-portfolio HCF/defaultQty denominator).
    // null until wired; the net-price getter then falls back to HCF-only (today's behaviour).
    @Input() ctx: IHedgingGridCopilotFacade | null = null;

    // Aggregate lock (CR Close-All increment): true while the "Close All / Chosen" card owns this hedge.
    // The child still ENTERS previewed/applied state (so it shows those badges — "receive the state of the
    // aggregated card"), but its OWN buttons are disabled so it cannot be toggled independently. This is a
    // SEPARATE flag from solution.state.blocked (which, for Select/Adjust, disables only Apply — reusing it
    // here would regress that one-of-N rule).
    @Input() aggregateLocked = false;

    // CR-20260918-001: the working-state book as read when this solution's run was fired. The
    // Transactions popup reads an Adjust package's BEFORE column from it (a card's legs are orders
    // and say nothing about what is held). A Close ignores it — its legs carry their own snapshot.
    @Input() heldAtRun: CoPilotPositionDto[] = [];

    // Optional unpriced proposal identity for automatic adjustments; prices always use the frontend quote cache.
    @Input() proposalSnapshot: AdjustmentProposal | null = null;
    @Input() placementBlocked = false;
    @Input() displayReference: string | null = null;
    @Output() proposalDisplayed = new EventEmitter<string>();
    private _lastDisplayed: string | null = null;
    private _destroyed = false;
    private _quoteTickers: string[] = [];

    @Output() previewToggle = new EventEmitter<Solution>();
    @Output() applyToggle = new EventEmitter<Solution>();
    @Output() openTransactions = new EventEmitter<Solution>();

    @ViewChild(HgCopilotTransactionsPopupComponent) txPopup: HgCopilotTransactionsPopupComponent;

    // The PACKAGE's own collapse (owner, 2026-07-27 — reversing the earlier "never collapses"
    // ruling once the collapsed form was settled). Collapsed it keeps only its name, its price and
    // its cards as CHIPS: the description, the package total and the action row all go, and the
    // chips drop their leg breakdown. Expanded it is the full package, and only then can each card
    // be opened and closed on its own.
    expanded = false;

    // Per-card expand state. Held on the CACHED view models below rather than in a parallel Set, so
    // a card's open/closed state cannot drift from the card it belongs to.
    private _cardsBuiltFrom: SolutionContent | null = null;
    private _cards: PackageCardVm[] = [];

    get previewLabel(): string {
        return this.solution.state.previewed ? 'Un-Preview' : 'Preview';
    }

    get applyLabel(): string {
        return this.solution.state.applied ? 'Un-Apply' : 'Apply';
    }

    // Preview/Apply workflow (owner): Apply is only available while this Solution is previewed, and you
    // cannot un-Preview a Solution that is still applied (un-Apply first). The two buttons gate each other.
    // A dead card (target hedge gone — see isTargetGone) blocks BOTH.
    get applyDisabled(): boolean {
        return this.aggregateLocked || this.isTargetGone || !this.solution.state.previewed;
    }

    get previewDisabled(): boolean {
        return this.aggregateLocked || this.isTargetGone || this.solution.state.applied
            || (this.placementBlocked && !this.solution.state.previewed);
    }

    // Transactions is read-only, but while the aggregate owns this hedge the child is fully locked
    // (the aggregate card carries its own Transactions view for the whole batch).
    get transactionsDisabled(): boolean {
        return this.aggregateLocked || this.isTargetGone;
    }

    // The distinct existing-hedge groupIds this Solution targets (CLOSE/Adjust legs carry groupId; Select
    // legs do not). Legs are an immutable Value, so no memoization needed.
    private solutionGroupIds(): string[] {
        const out: string[] = [];
        const legs = (this.solution && this.solution.content) ? this.solution.content.legs : [];
        (legs || []).forEach(l => {
            if (l.groupId && out.indexOf(l.groupId) === -1) {
                out.push(l.groupId);
            }
        });
        return out;
    }

    // True when this Solution's target hedge has been manually modified by the user — drives the yellow
    // border + "Modified" badge. The card is default-CD and re-checked in the parent's Hg.HedgeMatrixChanged
    // detectChanges pass, so it appears within ~100ms of the user's matrix edit. false for new-hedge (Select)
    // Solutions (no groupId on their legs) — nothing to modify.
    get isModified(): boolean {
        const ctx = this.ctx;   // local so the non-null narrowing survives into the .some() closure
        return !!ctx && this.solutionGroupIds().some(g => ctx.isCopilotHedgeModified(g));
    }

    // True when the existing hedge this Solution targets no longer exists in the book — e.g. the user
    // Acquired/closed it after previewing the CoPilot close. The card is then DEAD: all actions are blocked
    // and it shows a "Hedge removed" badge + red border. Distinct from isModified (which already returns
    // false once the hedge is gone). false for new-hedge (Select) Solutions (no groupId).
    get isTargetGone(): boolean {
        const ctx = this.ctx;
        if (!ctx) {
            return false;
        }
        const tickers = this.targetLegTickers();
        return tickers.length > 0 && ctx.isCopilotHedgeGone(tickers);
    }

    // The option tickers of this Solution's existing-hedge-target legs (CLOSE/Adjust legs carry groupId;
    // Select's new-hedge legs do not). Matched by TICKER against still-held book positions, because the
    // hedge groupId is NOT stable across the Acquire rebuild (see isCopilotHedgeGone in the matrix).
    //
    // ONLY legs that reference a HELD contract count — close / decrease / increase. A To-Open leg on a
    // target hedge (an attribution fold adding a contract the hedge never held) is EXPECTED to be unheld,
    // so it proves nothing about the hedge's existence. Before stage 7 every hedge-targeting leg was a
    // close and this filter changed nothing; without it, a fold-only transaction — all To-Open legs on
    // new strikes — read as "every ticker vanished" and the card went red for a hedge sitting alive on
    // the Matrix. A transaction with NO held-referencing legs simply opts out of the liveness check;
    // Preview still refuses a genuinely missing hedge by its own targetHedgeId lookup.
    private targetLegTickers(): string[] {
        const legs = (this.solution && this.solution.content) ? this.solution.content.legs : [];
        const out: string[] = [];
        (legs || []).forEach(l => {
            const referencesHeld = l.effect === 'close' || l.effect === 'decrease'
                || l.effect === 'increase' || l.effect === 'reverse';
            if (l.groupId && l.ticker && referencesHeld && out.indexOf(l.ticker) === -1) {
                out.push(l.ticker);
            }
        });
        return out;
    }

    // Signed quantity for the legs table: sell legs render negative, buy legs as-is (positive). The Action
    // cell still carries the full direction ("Sell To Close" / "Buy To Close"). Same sign rule the
    // Transactions popup's Transaction column uses.
    signedQuantity(leg: Leg): number {
        return leg.side === 'sell' ? -leg.quantity : leg.quantity;
    }

    // Display order for the legs table + codified preview: strike-DESCENDING (highest at top) — the
    // universal ETS visual rule (see compareLegStrikeDesc). Returns a COPY: the skill content is an
    // immutable Value rendered verbatim (ADR-029), and Apply/Preview read solution.content.legs
    // directly, so sorting for display must never mutate the source order.
    get sortedLegs(): Leg[] {
        const legs = (this.solution && this.solution.content) ? this.solution.content.legs : [];
        return (legs || []).slice().sort(compareLegStrikeDesc);
    }

    // The package's CARDS (CR-20260724-001). A package is an AGGREGATION of cards: each card stays a
    // card — its own box, its own title, its own legs, its own price — and only the ACTIONS move up,
    // because the cards of a package are necessary together and are never traded separately.
    //
    // Empty when the producer sent no cards (the LLM path, locally-generated Closes). The template
    // then renders the flat leg table exactly as it always did — see hasPackageCards.
    // Built ONCE per solution and cached: the template reads this every change-detection pass, and a
    // fresh array each time would both churn *ngFor and throw away each card's expand state.
    get packageCards(): PackageCardVm[] {
        const content = (this.solution && this.solution.content) ? this.solution.content : null;
        if (content !== this._cardsBuiltFrom) {
            this._cardsBuiltFrom = content;
            const previous = this._cards;
            this._cards = (content && content.cards ? content.cards : []).map((c, i) => ({
                title: c.title,
                legs: (c.legs || []).slice().sort(compareLegStrikeDesc),
                expanded: !!this.proposalSnapshot && previous[i]?.title === c.title && previous[i].expanded
            }));
        }
        return this._cards;
    }

    get hasPackageCards(): boolean {
        return this.packageCards.length > 0;
    }

    @DetectMethodChanges()
    toggleCard(card: PackageCardVm): void {
        card.expanded = !card.expanded;
    }

    // One card's OWN order price, computed exactly like the package's — same live mids, same
    // ES normalization, same all-or-nothing rule. A card is a real trade and shows what it costs.
    cardOrderPrice(card: PackageCardVm): number | undefined {
        return orderPrice(this.comboNetOf(card.legs));
    }

    // "3 legs" — the card's own leg count, the way the Close tab states one.
    cardLegsLabel(card: PackageCardVm): string {
        const n = card.legs.length;
        return n + (n === 1 ? ' Leg' : ' Legs');
    }

    // The card's expiration, taken from the display name the Instrument column already shows, so
    // the two can never disagree about how a date is written.
    cardExpiration(card: PackageCardVm): string {
        if (card.legs.length === 0) {
            return '';
        }
        const parts = (this.instrumentName(card.legs[0]) || '').split(' ');
        return parts.length > 0 ? parts[parts.length - 1] : '';
    }

    // "(+10)7425 / (+10)7410 / (-10)7405" — the same codified form the collapsed Close card uses,
    // scoped to ONE card's legs.
    cardCodifiedPreview(card: PackageCardVm): string {
        return card.legs.map(leg => {
            const q = this.signedQuantity(leg);
            const sign = q > 0 ? '+' : '';
            // Skill-authored legs carry no `strike` field — it is parsed back out of the ticker.
            // Reading leg.strike directly is why the package's own preview renders bare "(-20)"
            // with no strike for engine results.
            const k = legStrikeForSort(leg);
            const strike = isFinite(k) ? ('' + k) : '';
            return '(' + sign + q + ')' + strike;
        }).join(' / ');
    }

    // Codified transaction preview for the collapsed card: each leg as "(±qty)strike" joined by "/",
    // e.g. "(+1)7555/(-2)7545/(+2)7535". Qty is the signed order quantity (buy +, sell -) — same sign as
    // the expanded Qty column. Strike-DESC, identical order to the expanded table (this.sortedLegs).
    get codifiedPreview(): string {
        return this.sortedLegs.map(leg => {
            const q = this.signedQuantity(leg);
            const sign = q > 0 ? '+' : '';   // negatives already carry '-'
            const strike = (leg.strike !== undefined && leg.strike !== null) ? ('' + leg.strike) : '';
            return '(' + sign + q + ')' + strike;
        }).join(' / ');
    }

    @DetectMethodChanges()
    toggleExpanded(): void {
        this.expanded = !this.expanded;
    }

    ngOnInit(): void {
        // Subscribe each leg's ticker (ref-counted) so live quotes stream; re-render on every quote tick
        // by re-reading the cache from the template getters (the platform pattern — see
        // HgZonesGridSectionComponent.onQuoteDto). Legs are known once the @Input solution is bound.
        const tickers = this.legTickers();
        this._quoteTickers = tickers;
        if (tickers.length > 0) {
            this._lastQuoteCache.subscribeTickers(tickers);
        }
        this._messageBus.of<QuoteDto[]>('QuoteDto')
            .pipe(takeUntil(this._unsubscriber))
            .subscribe(() => this.onQuoteTick());
    }

    ngOnDestroy(): void {
        this._destroyed = true;
        const tickers = this._quoteTickers;
        if (tickers.length > 0) {
            this._lastQuoteCache.unsubscribeTickers(tickers);
        }
        this._unsubscriber.next();
        this._unsubscriber.complete();
    }

    ngAfterViewChecked(): void {
        const reference = this.displayReference;
        // Inspection means the full proposal was rendered, including its transaction legs.
        if (!reference || reference === this._lastDisplayed || !this.expanded
            || this.packageCards.some(c => !c.expanded)) { return; }
        Promise.resolve().then(() => {
            if (!this._destroyed && this.displayReference === reference && this.expanded
                && !this.packageCards.some(c => !c.expanded) && this._lastDisplayed !== reference) {
                this._lastDisplayed = reference;
                this.proposalDisplayed.emit(reference);
            }
        });
    }

    // Empty body: every 'QuoteDto' bus tick forces a CD pass (decorator), and the price getters below
    // re-pull fresh mid from the cache. Mirrors HgZonesGridSectionComponent.onQuoteDto.
    @DetectMethodChanges()
    onQuoteTick(): void {
    }

    // The instrument's platform display name ("SPX Call 7525 23-Jun-26"), resolved from the RAW ticker —
    // never the hand-built "Call 7525 2026-06-23".
    instrumentName(leg: Leg): string {
        return this._displayNames.getOptionDisplayName(leg.ticker);
    }

    // Current market price of the leg's instrument = the option mid (the platform's displayed option
    // price). Undefined until the quote starts streaming (blank for that render).
    legPrice(leg: Leg): number | undefined {
        return this.quoteMid(leg.ticker);
    }

    // The option mid for a raw ticker, read from the live quote cache. The single quote-read path —
    // both legPrice (template) and the shared comboNet pricing go through it.
    private quoteMid(ticker: string): number | undefined {
        const q = this._lastQuoteCache.getLastQuote(ticker);
        if (isVoid(q)) {
            return undefined;
        }
        return (typeof q.mid === 'number' && !isNaN(q.mid)) ? q.mid : undefined;
    }

    // Per-leg cash-flow price: a SELL receives premium (credit, +/green), a BUY pays it (debit, -/red).
    // Undefined until the quote streams.
    legSignedPrice(leg: Leg): number | undefined {
        const mid = this.legPrice(leg);
        if (mid === undefined) {
            return undefined;
        }
        return leg.side === 'sell' ? mid : -mid;
    }

    // Shared combo math (extracted to copilot-pricing.ts so the aggregate "Close All" card sums the
    // IDENTICAL per-hedge math — single source of truth). Returns the signed net + the HCF of the leg
    // quantities; undefined until ALL legs price (all-or-nothing). normFactor is the per-leg ES->SPX
    // multiplier from the grid facade (ctx).
    private comboNet(): ComboNet | undefined {
        const legs = (this.solution && this.solution.content) ? this.solution.content.legs : [];
        return this.comboNetOf(legs);
    }

    // The same math over ANY leg set, so a card inside a package prices identically to the package
    // itself — one implementation, no chance of a card and its parent disagreeing about a mid.
    private comboNetOf(legs: Leg[]): ComboNet | undefined {
        return computeComboNet(
            legs,
            ticker => this.quoteMid(ticker),
            asset => this.ctx ? this.ctx.getNormFactor(asset) : 1
        );
    }

    // Order price = net / HCF (minimal HCF-reduced combo, no portfolio default-qty normalization) — the
    // "Order Placement Price". Shown in the expanded Total row. Blank until ALL legs price.
    get orderComboPrice(): number | undefined {
        return orderPrice(this.comboNet());
    }

    // Ledger price (credit > 0 / debit < 0) = the order price scaled to the portfolio default quantity
    // (net / defaultQty). Shown in the card header (top-right) and summed for the aggregate. Blank until
    // ALL legs price.
    get netComboPrice(): number | undefined {
        const defaultQty = this.ctx ? this.ctx.readDefaultQty() : undefined;
        return ledgerPrice(this.comboNet(), defaultQty);
    }

    // "(9 legs)" — the package's own leg count, stated in the title bar so the package can be judged
    // at a glance (title · legs · price) without opening it (owner, 2026-08-19).
    //
    // ⚠ This is the TRADED NET (content.legs), never the sum of the cards' leg counts (owner's
    // ruling, 2026-08-19). For a classic package the two are identical — its cards are that same
    // order split back across the blocks that produced it. For an [alt] package they can DIFFER:
    // its cards are partitioned per PHASE and are never merged across phases, so a strike two
    // phases both touch appears on both cards while netting once in the order. The header states
    // what actually gets sent — which is also what the ranking tie-breaks on.
    get packageLegsLabel(): string {
        const content = (this.solution && this.solution.content) ? this.solution.content : null;
        const n = (content && content.legs) ? content.legs.length : 0;
        return '(' + n + (n === 1 ? ' Leg' : ' Legs') + ')';
    }

    // A NAKED LEG in this package = a card whose traded order is ONE leg (owner 2026-09-03): that
    // order closes into no shape, so the trader must execute it before the other orders. Judged
    // on the cards (each card = one order), never on the netted package legs.
    get hasNakedLeg(): boolean {
        return this.nakedCards().length > 0;
    }

    /** The naked cards' titles, for the expanded warning: "Put Slingshot" / "A, B". */
    get nakedCardTitles(): string {
        return this.nakedCards().map(c => c.title).join(', ');
    }

    /** The card IS the naked-leg order: its traded order is one leg. Marked on the card itself. */
    isNakedCard(card: { legs: unknown[] }): boolean {
        return (card.legs || []).length === 1;
    }

    private nakedCards(): { title: string; legs: unknown[] }[] {
        const cards = (this.solution && this.solution.content && this.solution.content.cards) || [];
        return cards.filter(c => (c.legs || []).length === 1);
    }

    // Dollar-formatted price: "$1.25" / "-$2.00"; blank ("—") when not yet priced.
    formatMoney(value: number | undefined): string {
        return formatComboMoney(value);
    }

    private legTickers(): string[] {
        const legs = (this.solution && this.solution.content) ? this.solution.content.legs : [];
        return (legs || []).map(l => l.ticker).filter(t => !isVoid(t));
    }

    // The full (extended) solution description, shown in the EXPANDED card without a toggle. Falls back to
    // the short description if the skill provided no extended text. (The short description is the COLLAPSED
    // summary; the expanded card shows the full description only.)
    get fullDescription(): string {
        const content = this.solution && this.solution.content;
        if (!content) {
            return '';
        }
        return content.extendedDescription || content.shortDescription || '';
    }

    onPreview(): void {
        this.previewToggle.emit(this.solution);
    }

    // Always emit — a Blocked Solution's Apply is refused by the parent with a plain message (FR-91),
    // never silently (NFR-22). FR-89 "disable the Apply action" = it cannot succeed, not that it is mute.
    onApply(): void {
        this.applyToggle.emit(this.solution);
    }

    onTransactions(): void {
        this.openTransactions.emit(this.solution);
        // Imperative show() — the popup is opened via its @DetectMethodChanges-decorated show() (this
        // codebase drives change detection through decorators, not automatic CD / markForCheck).
        this.txPopup.show(this.solution, this.heldAtRun);
    }
}
